<script lang="ts">
    import {getLogger} from 'src/core';
    import type {SingleAcceptableValueEditorLocalizations, SingleAcceptableValueEditorOptions} from './types';
    import type {ValueEditorValidations} from '../../../kp-value-editor/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormControlOptions} from '../../forms/types';
    import type {ValueEditorSize} from '../../../types';
    import {getContext, onDestroy} from 'svelte';
    import type {FormControl} from '../../forms/form-control';
    import {FormControlBuilder} from '../../forms/form-control';
    import {createInputDescription} from '../../description/utils';
    import {RadioGroup, RadioGroupOption} from '@rgossiaux/svelte-headlessui';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {style} from 'svelte-forms';
    import {addAttributes} from '../../shared/use.add-attributes';
    import {exists, isFunction, isNullOrUndefined, isUndefined} from 'shared/utils/custom-utils';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import KpDropdownListbox from '../../../../ui-widgets/dropdown-listbox/KpDropdownListbox.svelte';
    import DescriptionIdProvider from '../../description/DescriptionIdProvider.svelte';
    import LabelIdProvider from '../../label/LabelIdProvider.svelte';
    import DropdownListboxLabelProvider from '../../../../ui-widgets/dropdown-listbox/DropdownListboxLabelProvider.svelte';
    import NullableOptionLabel from './NullableOptionLabel.svelte';
    import {
        arrayToScalarValueFormatter,
        getOptionById,
        getOptionId,
        getOptionIdResolver,
        modelToIdentifierFormatter,
        scalarValueToArrayParser,
        identifierToModelParser,
        validatorFactory, tryToFindOptionById
    } from './utils';

    type MODEL_VALUE = $$Generic;
    type OptionIdentifier = number | string;

    export let model: MODEL_VALUE | MODEL_VALUE[];
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: SingleAcceptableValueEditorOptions<MODEL_VALUE>;
    export let validations: ValueEditorValidations;


    model = normalizeModelIfNeeded(model);

    const localize: EditorsLocalizationFunction<SingleAcceptableValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    onDestroy(() => {
        modelValueSubscription.unsubscribe();
        viewValueSubscription.unsubscribe();
    });

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withOptions(formControlOptions)
        .withValidators([...validatorFactory(validations)])
        .withParsers([identifierToModelParser(() => options.acceptableValues, options), ...options.modelAsArray ? [scalarValueToArrayParser()] : []])
        .withFormatters([modelToIdentifierFormatter(options), ...options.modelAsArray ? [arrayToScalarValueFormatter()] : []])
        .build(getLogger());

    $: model = normalizeModelIfNeeded(model);

    const modelValueSubscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const viewValue$ = formControl.getViewValue$();
    let currentViewValue: OptionIdentifier;
    let lastViewValue: OptionIdentifier; // to distinguish between change from model and change from `currentViewValue` prop binding
    const viewValueSubscription = viewValue$.subscribe((viewValue) => {
        currentViewValue = viewValue;
        lastViewValue = viewValue;
    });
    $: {
        if (currentViewValue !== lastViewValue) {
            // changed by RadioGroup by prop binding
            selectOption(currentViewValue);
        }
    }

    let inlineMode: boolean;
    $: inlineMode = options.switchToInlineModeThreshold !== 0 && options.acceptableValues.length >= options.switchToInlineModeThreshold;

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    function normalizeModelIfNeeded(modelValue: MODEL_VALUE | MODEL_VALUE[]): MODEL_VALUE | MODEL_VALUE[] {
        if (!options.modelAsArray) {
            return modelValue;
        }

        if (Array.isArray(modelValue)) {
            if (modelValue.length > 1) { // [a, b, ...]
                return modelValue.slice(0, 1); // [a]
            }
            if (modelValue.length < 1) { // []
                return null;
            }
            return modelValue;
        } else {
            if (modelValue === null) {
                return null;
            } else {
                return [modelValue];
            }
        }
    }

    function selectOption(optionIdentifier: OptionIdentifier): void {
        formControl.setViewValue(optionIdentifier);
    }

    $: allowNullValue = options.allowSelectNull && !validations.required;

    let selectableOptionsIdentifiers: OptionIdentifier[];
    $: selectableOptionsIdentifiers = createSelectableOptions(options.acceptableValues, allowNullValue, options.sortComparator);

    function createSelectableOptions(acceptableValues: typeof options.acceptableValues,
                                     addNullOption: boolean,
                                     comparator?: typeof options.sortComparator): OptionIdentifier[] {
        const acceptableValuesCopy = [...acceptableValues];
        if (isFunction(comparator)) {
            try {
                acceptableValuesCopy.sort((element1, element2) => comparator({element1, element2}));
            } catch (e) {
                throw new Error(`Error in custom sortComparator: ${e}`);
            }
        }
        const selectableOptionsIds = acceptableValuesCopy.map((option) => getOptionId(option, getOptionIdResolver(options)));
        return addNullOption ? [...selectableOptionsIds, null] : selectableOptionsIds;
    }

    function isOptionDisabled(optionIdentifier: OptionIdentifier): boolean {
        if (!isFunction(options.optionDisabledResolver)) {
            throw new Error(`disabledItemResolver is not function: ${options.optionDisabledResolver}`);
        }

        const option = getOptionById(options.acceptableValues, getOptionIdResolver(options), optionIdentifier);
        try {
            return options.optionDisabledResolver({options, item: option, model});
        } catch (e) {
            throw new Error(`Error in custom optionDisabledResolver: ${e}`);
        }
    }

    function addModelValueIfNotInOptions(acceptableValues: MODEL_VALUE[]): MODEL_VALUE[] {
        const optionIdResolver = getOptionIdResolver(options);
        const modelValue = options.modelAsArray ? model[0] : model; // model can be scalar or array of single element
        const option = tryToFindOptionById(acceptableValues, optionIdResolver, getOptionId(modelValue, optionIdResolver));

        if (isUndefined(option)) {
            return [...acceptableValues, modelValue];
        }

        return acceptableValues;
    }

    function getCurrentModelOption(optionIdentifier: OptionIdentifier): MODEL_VALUE {
        return getOptionById(addModelValueIfNotInOptions(options.acceptableValues), getOptionIdResolver(options), optionIdentifier);
    }

    const addAttributesOptions = new Map<string, any>([
        ['name', editorName],
        ['aria-required', validations.required]
    ]);

    export function getFormController(): FormControl<typeof model> {
        return formControl;
    }
</script>

{#if inlineMode}
    <KpDropdownListbox value={currentViewValue}
                       id={editorId}
                       {size}
                       options={selectableOptionsIdentifiers}
                       additionalButtonClass="form-control single-acceptable-inline-button form-control-button"
                       disabled={isDisabled || selectableOptionsIdentifiers.length === 1 && !exists(model)}
                       {isOptionDisabled}
                       use={[
                           [addAttributes, addAttributesOptions],
                           [touched, {formControl}],
                           [focusOnMount, isFocused],
                           [ariaInvalid, formControl],
                           [ariaErrormessage, formControl],
                           [ariaDescribedby, inputDescription],
                           [style, {field: formControl.getFieldStateAsStore()}]]}
                       data-main-input
                       on:change={(event) => selectOption(event.detail)}>

        <svelte:fragment slot="label">
            <DropdownListboxLabelProvider/>
        </svelte:fragment>

        <svelte:fragment slot="button">
            <span class="input-button-label">
                {#if isNullOrUndefined(currentViewValue) && (!allowNullValue || !!placeholder)}
                    <span class="placeholder">{placeholder}</span>
                {:else}
                    <NullableOptionLabel optionIdentifier={currentViewValue} let:optionIdentifier>
                        <svelte:component this={options.optionLabelComponent}
                                          option={getCurrentModelOption(optionIdentifier)}
                                          usedAsLabel>
                        </svelte:component>
                    </NullableOptionLabel>
                {/if}
            </span>
            <span aria-hidden="true" class="caret"></span>
        </svelte:fragment>

        <span class="option-label" slot="option" let:option>
            <NullableOptionLabel optionIdentifier={option} let:optionIdentifier>
                <svelte:component this={options.optionLabelComponent}
                                  option={getOptionById(options.acceptableValues, getOptionIdResolver(options), optionIdentifier)}>
                </svelte:component>
            </NullableOptionLabel>
        </span>
    </KpDropdownListbox>

    <ErrorMessages formController={formControl} {forceShowErrors} {size} customClass="single-acceptable-select-error"/>
{:else}
    <RadioGroup bind:value={currentViewValue}
                data-id={editorId}
                data-name={editorName}
                class="list-group size-{size} {options.blockInRow === true ? 'list-group-horizontal' : ''}"
                disabled={isDisabled}
                aria-required={validations.required}
                use={[
                    [touched, {formControl, touchTriggerEvent: 'focusout', isDisabled}],
                    [focusOnMount, isFocused],
                    [ariaInvalid, formControl],
                    [ariaErrormessage, formControl],
                    [style, {field: formControl.getFieldStateAsStore()}]]}
                data-main-input>

        <LabelIdProvider/>

        {#each selectableOptionsIdentifiers as selectableOptionIdentifier (selectableOptionIdentifier)}
            <RadioGroupOption as="button"
                              type="button"
                              disabled={isDisabled || isOptionDisabled(selectableOptionIdentifier)}
                              class={`list-group-item ${
                                  currentViewValue === selectableOptionIdentifier ? 'active' : ''} ${
                                  isDisabled || isOptionDisabled(selectableOptionIdentifier) ? 'disabled' : ''}`}
                              value={selectableOptionIdentifier}>
                <span class="option-label">
                    <NullableOptionLabel optionIdentifier={selectableOptionIdentifier} let:optionIdentifier>
                        <svelte:component this={options.optionLabelComponent}
                                          option={getOptionById(options.acceptableValues, getOptionIdResolver(options), optionIdentifier)}
                                          usedAsLabel>
                         </svelte:component>
                    </NullableOptionLabel>
                </span>
            </RadioGroupOption>
        {/each}

        <DescriptionIdProvider {inputDescription}/>
    </RadioGroup>

    <ErrorMessages formController={formControl} {forceShowErrors} {size}/>
{/if}

<Description {inputDescription}/>

<style lang="less" global>
    @import (reference) "bootstrap-less/bootstrap/variables";

    @disabled-opacity: 0.5;

    kp-value-editor[data-type="single-acceptable"] {

        button.list-group-item.active.disabled {
            opacity: @disabled-opacity;
        }

        // used patch version of bootstrap does not highlight buttons in list group
        // code copied from newer version, but due to the selector specificity
        // it overrides styles of .active state, so apply style only if item is not .active or .disabled
        button.list-group-item:not(.active):not(.disabled) {
            color: @list-group-link-color;

            // Hover state
            &:hover,
            &:focus {
                color: @list-group-link-hover-color;
                text-decoration: none;
                background-color: @list-group-hover-bg;
            }
        }

        button.list-group-item {
            width: 100%;
        }

        .list-group {
            margin-bottom: 0;

            &.size-md {
                .list-group-item {
                    padding-top: 6px;
                    padding-bottom: 6px;
                }
            }

            &.size-sm,
            &.size-xs {
                .list-group-item {
                    font-size: 12px;
                    line-height: 1.5;
                }
            }

            &.size-sm {
                .list-group-item {
                    padding-top: 6px;
                    padding-bottom: 4px;
                }
            }

            &.size-xs {
                .list-group-item {
                    padding-top: 2px;
                    padding-bottom: 0;
                }
            }
        }

        .list-group-horizontal {
            display: flex;
            align-items: center;

            .list-group-item {
                margin-bottom: 0;
                transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out, border 0.2s ease-in-out;

                &:not(:last-child) {
                    border-right: none;
                }

                &:first-child {
                    border-radius: 4px 0 0 4px;
                }

                &:last-child {
                    border-radius: 0 4px 4px 0;
                }

                &.active {
                    background-color: var(--accent-blue);
                    border-color: var(--accent-blue);
                }
            }
        }

        .input-button-label {
            margin-right: 12px; // to ensure gap between label and caret
        }

        .caret {
            position: absolute;
            height: 10px;
            top: 50%;
            right: 10px;
            margin-top: -2px;
        }

        .input-xs {
            .caret {
                right: 5px;
            }
        }

        .error-message.single-acceptable-select-error {
            margin-top: 1px;
        }

        .single-acceptable-inline-button {
            text-align: left;
            width: 100%; // to override .form-control `width: auto` in media queries
        }
    }

    .has-error {
        kp-value-editor[data-type="single-acceptable"] {
            .list-group .list-group-item {
                border-left-color: @state-danger-text;
                border-right-color: @state-danger-text;
                transition: border-color 0.15s ease-in-out;

                &:first-child {
                    border-top-color: @state-danger-text;
                }

                &:last-child {
                    border-bottom-color: @state-danger-text;
                }
            }
        }

        /* reset error styles if editor is nested in meta-editor which has error */

        .has-not-error {
            kp-value-editor[data-type="single-acceptable"] {
                .list-group .list-group-item {
                    border-color: @list-group-border;
                }
            }
        }
    }
</style>
